import { useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "underscore";
import { useAtomValue } from "jotai";
import { useParams } from "react-router-dom";

import StrUtils from "../../../utils/StrUtils";
import TenantAPI from "../../api/TenantAPI";
import {
  cloudRelatedRecordsAtom,
  queriesEnabledAtom,
  tenantIDAtom,
} from "../../../atoms/GlobalAtoms";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import useWorkspace from "../workspaces/useWorkspace";
import useFieldMap from "../../../hooks/useFieldMap";
import QueryUtils from "../../../utils/QueryUtils";
import { generateTimePrefills } from "../../../utils/QuickCreateUtils";
import { hasKeys } from "../../../utils/GeneralUtils";
import useUser from "../auth/useUser";
import useTenantUsers from "../tenantUsers/useTenantUsers";
import SharedScreenAPI from "../../api/SharedScreenAPI";

export type Intervals =
  | "year"
  | "quarter"
  | "week"
  | "month"
  | "day"
  | "hour"
  | "minute"
  | "second";

interface Props {
  moduleAPIName: string;
  query: any;
  variableValues?: any;
  queryValidator?: (query: any) => boolean;
  filterAggregations?: boolean;
  disabled?: boolean;
  topLevelFilters?: any;
  viewFilters?: any;
  cloudID?: string;
  groupByQueryField?: string; // For GroupBy queries involving Table Group By, Kanban, and Timeline views.
  groupByInterval?: Intervals;
  useDefaultVariables?: boolean; // user, workspace variable values automatically provided when this is true!
  timeZone?: string;
  queryKeyID?: string;
}

// ---- useQueriedRecords - The MOST important RQ hook in GrowthCloud to date. ----
//
// 1 - Validate query and other provided Props. We won't do anything else if they are not valid.
// 2 - If fields isn't *, and tags isn't included, add tags (Tags are a default field that is commonly needed.)
// 3 - If filter aggregations is true, copy query and delete the Aggregations property
// 4 - If we are inside of a workspace, ensure the id isn't "None" and the workspace isn't still loading. This enforces the workspace filters feature.
// 5 - If there are workspace filters that exist in the provided module, merge the top level workspace filters with the query.
// 6 - If there are submodule filters that exist, merge the top level submodule filters with the query.
// 7 - If we did any merging in steps 4/5, set finalQuery variable to the result of 4/5.
// 8 - If there are variable values, check for Dynamic values and fill in appropriately.
// 9 - Check for DateTime dynamic values and fill in appropriately.
function useQueriedRecords({
  moduleAPIName,
  query,
  variableValues, // Provide this object prop when allowing dynamic values: {key: value}
  queryValidator,
  filterAggregations = false,
  disabled = false,
  topLevelFilters = [],
  viewFilters = [],
  cloudID,
  groupByQueryField,
  groupByInterval,
  timeZone,
  useDefaultVariables = false, // Instead of providing variableValues prop, you can set this to true to get default variableValues (user, cloud values, workspace values)
  queryKeyID, // Optional ID to mark a Query Cache with. This allows us to refresh a specific query easier.
}: Props) {
  const {
    formID,
    tenantID,
    sharedViewID,
    sharedPageID,
    sharedRecordPageID,
    viewID,
  } = useParams();
  const queryClient = useQueryClient();
  const tenantIDAtomValue = useAtomValue(tenantIDAtom);
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const relatedRecords = useAtomValue(cloudRelatedRecordsAtom);
  const { workspace, workspaceLoading } = useWorkspace();
  const { tenantUsersByRole } = useTenantUsers();
  const { user, userLoading } = useUser();
  cloudID =
    formID || sharedViewID || sharedPageID || sharedRecordPageID
      ? tenantID
      : cloudID;

  const fieldTypeMap = useFieldMap(moduleAPIName, "FieldType");
  const isSupportal = window.location.pathname.startsWith("/supportal");
  const isPublicForm = window.location.pathname.startsWith("/forms");
  const isSharedView = window.location.pathname.startsWith("/shared-view");
  const isSharedPage = window.location.pathname.startsWith("/shared-page");
  const isSharedRecordPage = window.location.pathname.startsWith(
    "/shared-record-page"
  );

  let isReady: boolean =
    queriesEnabled ||
    isSupportal ||
    isPublicForm ||
    isSharedView ||
    isSharedPage ||
    isSharedRecordPage; // Used to disable the useQuery hook.
  isReady = isReady && workspaceID ? !workspaceLoading : true;
  isReady = isReady && (queryValidator ? !!queryValidator(query) : true);
  isReady = isReady && !!moduleAPIName && disabled === false; // Validate other Props.
  isReady = isReady && hasKeys(query, null);
  isReady = isReady && (useDefaultVariables ? !userLoading : true);

  let finalQuery = isReady ? structuredClone(query) : query;
  const tFs = topLevelFilters;
  const vFs = viewFilters;
  const wFs = workspace?.Filters;
  const topFilters = isReady && tFs?.length ? structuredClone(tFs) : tFs;
  const vwFilters = isReady && vFs?.length ? structuredClone(vFs) : vFs;
  const wsFilters = isReady && wFs?.length ? structuredClone(wFs) : wFs;
  const fields = finalQuery?.Fields;

  if (isReady && Array.isArray(fields)) {
    if (!fields.includes("*") && !fields.includes("Tags")) {
      finalQuery.Fields.push("Tags");
    }
  }

  if (isReady && filterAggregations) delete finalQuery.Aggregations;

  // Merge in any top level workspace filters
  if (
    isReady &&
    !isSupportal &&
    workspaceID &&
    workspaceID !== "None" &&
    !workspaceLoading &&
    workspace?.Filters?.length > 0
  ) {
    const filter = _.findWhere(wsFilters, { ModuleAPIName: moduleAPIName });
    const workspaceFilter = filter?.Filter?.Filters;

    finalQuery = QueryUtils.mergeTopLevelFilter(workspaceFilter, finalQuery);
  }

  // Merge in any Top level filters if provided
  if (isReady && topFilters.length > 0) {
    finalQuery = QueryUtils.mergeTopLevelFilter(topFilters, finalQuery);
  }

  // Merge in any View top level filters if provided
  if (isReady && vwFilters.length > 0) {
    finalQuery = QueryUtils.mergeTopLevelFilter(vwFilters, finalQuery);
  }

  // If useDefaultVariables is true, set variableValues to the default variable.
  if (isReady && useDefaultVariables === true) {
    variableValues = {
      ...(variableValues || {}),
      workspace: tenantUsersByRole,
      user: user,
    };
  }

  // If variable values are provided, check / fill in dynamic values
  if (
    isReady &&
    variableValues !== null &&
    typeof variableValues === "object" &&
    Object.keys(variableValues)?.length
  ) {
    const cloudRecords = relatedRecords?.reduce((obj: any, record) => {
      obj[record._ModuleAPIName] = record;
      return obj;
    }, {});

    variableValues.cloud = cloudRecords;

    finalQuery = fillVariables(finalQuery, variableValues, fieldTypeMap);
  }

  if (isReady) {
    finalQuery = handleTimeDynamicValues(finalQuery);
  }

  if (isReady && groupByQueryField) {
    finalQuery.GroupBy = { Field: groupByQueryField };
    finalQuery.GetAllGrouped = true;

    if (groupByInterval) {
      finalQuery.GroupBy.Interval = groupByInterval;
    }

    if (timeZone) {
      finalQuery.GroupBy.TimeZone = timeZone;
    }
  }

  // Use custom search function built for public forms when in public form route or shared view route.
  const SearchRecordsPromise: any = isPublicForm
    ? TenantAPI.searchRecordsWithForm
    : isSharedView
    ? SharedScreenAPI.searchRecordWithSharedView
    : isSharedPage
    ? SharedScreenAPI.searchRecordWithSharedPage
    : isSharedRecordPage
    ? SharedScreenAPI.searchRecordWithSharedRecordPage
    : TenantAPI.searchRecords;

  const queryKey = [
    cloudID || tenantIDAtomValue || "",
    "records",
    moduleAPIName,
    finalQuery,
  ];

  queryKeyID && queryKey.push(queryKeyID);

  const records: any = useQuery({
    queryKey: queryKey,
    queryFn: () => {
      return SearchRecordsPromise({
        moduleAPIName: moduleAPIName,
        query: finalQuery,
        tenantID: cloudID,
        formID: formID,
        sharedViewID: sharedViewID,
        sharedPageID: sharedPageID,
        sharedRecordPageID: sharedRecordPageID,
        viewID: viewID,
      }).then(
        (res: any) => {
          const { pagination, data } = res.data.data;

          if (!finalQuery?.Pagination?.Page) {
            return { records: data, pagination };
          }

          if (pagination?.NextPage) {
            // Prefetch the next page on success
            const nextPageQuery = {
              ...finalQuery,
              Pagination: {
                ...finalQuery.Pagination,
                Page: finalQuery.Pagination.Page + 1,
              },
            };

            queryClient.prefetchQuery({
              queryKey: [
                cloudID || tenantIDAtomValue || "",
                "records",
                moduleAPIName,
                nextPageQuery,
              ],
              queryFn: () =>
                SearchRecordsPromise({
                  moduleAPIName: moduleAPIName,
                  query: nextPageQuery,
                  tenantID: cloudID,
                  formID: formID,
                  sharedViewID: sharedViewID,
                  sharedPageID: sharedPageID,
                  viewID: viewID,
                }).then(
                  (res: any) => {
                    const { pagination, data } = res.data.data;

                    return { records: data, pagination };
                  },
                  (e: any) => {
                    return {
                      records: [],
                      error: true,
                      errorMsg:
                        e?.response?.message ??
                        "There was an error performing your search, please reload and try again.",
                    };
                  }
                ),
            });
          }

          return { records: data, pagination };
        },
        (e: any) => {
          return {
            records: [],
            error: true,
            errorMsg:
              e?.response?.message ??
              "There was an error performing your search, please reload and try again.",
          };
        }
      );
    },
    enabled: !!isReady,
  });

  return {
    records: records.data?.records ?? [],
    pagination: records.data?.pagination ?? null,
    recordsLoading:
      records.isLoading ||
      (queriesEnabled === false &&
        !isSupportal &&
        !isPublicForm &&
        !isSharedView &&
        !isSharedPage &&
        !isSharedRecordPage) ||
      !isReady,
    error: records?.data?.error ? true : false,
    errorMsg: records?.data?.errorMsg,
    queryKey: queryKey,
  };
}

// Loop over Query. Check for Filter Groups vs Filters - And fill in Dynamic values if needed.
export function fillVariables(
  query: any,
  variableVals: any,
  fieldTypeMap: any
) {
  const out = query;

  for (let i = 0; i < out?.Filters?.length; i++) {
    let filterGroup = out?.Filters[i];

    if (filterGroup?.Group) {
      for (let j = 0; j < filterGroup?.Filters?.length; j++) {
        let filter = filterGroup?.Filters[j];

        if (filter?.Value) {
          filter = subVariableForValue(filter, variableVals, fieldTypeMap);
        }
      }
    } else if (filterGroup?.Value) {
      filterGroup = subVariableForValue(
        filterGroup,
        variableVals,
        fieldTypeMap
      );
    }
  }

  return out;
}

function subVariableForValue(
  filter: any,
  variablesVals: any,
  fieldTypeMap: any
) {
  // TODO: Check this IF block... might be erroneous.
  if (_.isArray(filter?.Value)) {
    for (let i = 0; i < filter?.Value?.length; i++) {
      filter.Value[i] = StrUtils.templateConverter(
        filter?.Value[i],
        variablesVals
      );
    }
  } else {
    try {
      // These template settings work for strings that start with "{{" and end with "}}"
      _.templateSettings = { interpolate: /\{\{(.+?)\}\}/g };

      const valTemplate = _.template(filter?.Value);

      try {
        let val = valTemplate(variablesVals);

        // Handle special dynamic values
        val = handleCustomDynamicValues(
          val,
          filter,
          variablesVals,
          fieldTypeMap
        );

        filter.Value = val;
      } catch (e) {}
    } catch (e) {}
  }

  return filter;
}

function handleCustomDynamicValues(
  val: any,
  filter: any,
  variablesVals: any,
  fieldTypeMap: any
) {
  const isMultiReference = fieldTypeMap?.[filter?.Field] === "MultiReference";
  if (filter?.Value?.indexOf("record.id") >= 0) {
    val = {
      ModuleAPIName: variablesVals?.record?._ModuleAPIName,
      id: variablesVals?.record?.id,
      _RecordName: variablesVals?.record?._RecordName,
    };

    if (isMultiReference) val = [val];
  } else if (filter?.Value?.indexOf("user.id") >= 0) {
    val = {
      ModuleAPIName: "Contacts",
      id: variablesVals?.user?.id,
    };

    if (isMultiReference) val = [val];
  } else if (filter?.Value?.includes("workspace.")) {
    const pattern = /{{workspace\.(\w+)}}/;
    const workspaceRole = filter?.Value.match(pattern)[1];

    val = { id: variablesVals?.workspace?.[workspaceRole] };
    if (isMultiReference) val = [val];
  } else if (filter?.Value?.includes("cloud.")) {
    const pattern = /{{cloud\.(\w+)}}/;
    const cloudModule = filter?.Value.match(pattern)[1];
    const cloudRecord = variablesVals?.cloud?.[cloudModule];

    val = {
      ModuleAPIName: cloudModule,
      id: cloudRecord?.id,
      _RecordName: cloudRecord?._RecordName,
    };
    if (isMultiReference) val = [val];
  }

  return val;
}

// Checks for Dynamic values that start with {{time. and fills them in accordingly
function handleTimeDynamicValues(finalQuery: any) {
  const filters = finalQuery?.Filters;

  if (!filters || !Array.isArray(filters)) return finalQuery;

  let changeOccurred = false;

  const finalFilters = filters?.map((filterGroup: any) => {
    if (filterGroup?.Group) {
      let nestedChangeOccurred = false;

      const newFilter = filterGroup?.Filters?.map((f: any) => {
        if (!f?.Value?.startsWith?.("{{time.")) return f;

        const timeValues = generateTimePrefills({
          value: f.Value,
          method: f.Method,
        });

        if (!timeValues) return filterGroup;

        delete f.Value;

        nestedChangeOccurred = true;
        changeOccurred = true;

        return { ...f, ...timeValues };
      });

      if (!nestedChangeOccurred) return filterGroup;

      return { ...filterGroup, Filters: newFilter };
    } else {
      if (!filterGroup?.Value?.startsWith?.("{{time.")) return filterGroup;

      const timeValues = generateTimePrefills({
        value: filterGroup.Value,
        method: filterGroup.Method,
      });

      if (!timeValues) return filterGroup;

      delete filterGroup.Value;

      changeOccurred = true;

      return { ...filterGroup, ...timeValues };
    }
  });

  if (!changeOccurred) return finalQuery;

  return { ...finalQuery, Filters: finalFilters };
}

export default useQueriedRecords;
